import * as React from 'react';
import { createContext, type ReactNode, useContext, useState } from 'react';

type Language = 'ko' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  ko: {
    // Navigation
    'nav.blog': '블로그',
    'nav.about': '소개',

    // Index page
    'index.greeting': '안녕하세요',
    'index.description': '개발 여정에서 마주친 배움의 조각들을 모아둡니다.',
    'index.all': '전체',
    'index.noPostsForTag': '해당 태그의 글이 없습니다.',
    'index.loadMore': '더보기',

    // About page
    'about.title': '소개',

    // 404 page
    '404.title': '페이지를 찾을 수 없습니다',
    '404.description': '요청하신 페이지가 존재하지 않거나 이동되었을 수 있습니다.',
    '404.backHome': '홈으로',
    '404.goBack': '뒤로가기',

    // Common
    'common.readMore': '자세히 보기',
  },
  en: {
    // Navigation
    'nav.blog': 'Blog',
    'nav.about': 'About',

    // Index page
    'index.greeting': 'Hello',
    'index.description': 'A space to record what I learn while developing.',
    'index.all': 'All',
    'index.noPostsForTag': 'No posts with this tag.',
    'index.loadMore': 'Load More',

    // About page
    'about.title': 'About',

    // 404 page
    '404.title': 'Page Not Found',
    '404.description': "The page you're looking for doesn't exist or has been moved.",
    '404.backHome': 'Go Home',
    '404.goBack': 'Go Back',

    // Common
    'common.readMore': 'Read More',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('ko');

  const toggleLanguage = () => {
    setLanguage(prev => (prev === 'ko' ? 'en' : 'ko'));
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>{children}</LanguageContext.Provider>
  );
};

LanguageProvider.displayName = 'LanguageProvider';

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
